'use client';

import { signOut as _signOut,
  signInWithPopup as _signInWithPopup,
  updatePassword as _updatePassword,
  EmailAuthProvider as _EmailAuthProvider,
  GoogleAuthProvider as _GoogleAuthProvider,
  FacebookAuthProvider as _FacebookAuthProvider,
  sendEmailVerification as _sendEmailVerification,
  sendPasswordResetEmail as _sendPasswordResetEmail,
  reauthenticateWithCredential as _reauthenticateWithCredential,
  signInWithEmailAndPassword as _signInWithEmailAndPassword,
  createUserWithEmailAndPassword as _createUserWithEmailAndPassword,
} from "firebase/auth";
import { firebaseClientAuth } from "@/src/lib/firebase/clientApp";
import axios from 'src/lib/axios';
import { paths } from "@/src/routes/paths";

// ----------------------------------------------------------------------

// orgId is no longer required at signup - users join organisations during onboarding

export type SignInParams = {
  email: string;
  password: string;
};

export type SignUpParams = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  personNummer?: string;
  telefonNummer?: string;
};

export type ForgotPasswordParams = {
  email: string;
};

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({
  email,
  password,
}: SignInParams): Promise<void> => {
  try {
    await _signInWithEmailAndPassword(firebaseClientAuth, email, password);

    const user = firebaseClientAuth.currentUser;
  } catch (error) {
    console.error("Error during sign in with password:", error);
    throw error;
  }
};

export const signInWithGoogle = async (): Promise<any> => {
  const provider = new _GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await _signInWithPopup(firebaseClientAuth, provider);
  const user = result.user;

  if (user) {
    const names = (user.displayName || "").split(" ");
    const firstName = names[0] || "";
    const lastName = names.slice(1).join(" ") || "";
    const token = await user.getIdToken();
    localStorage.setItem('accessToken', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;

    try {
      await axios.post('/api/anvandare/v1', {
        email: user.email,
        förnamn: firstName,
        efternamn: lastName,
        personNummer: null, // To be filled during onboarding
        telefonNummer: null, // To be filled during onboarding
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } catch (e) {
      console.error("Error creating user in backend", e);
    }
  }
  return result;
};

export const signInWithFacebook = async (): Promise<any> => {
  const provider = new _FacebookAuthProvider();
  const result = await _signInWithPopup(firebaseClientAuth, provider);
  const user = result.user;

  if (user) {
    const names = (user.displayName || "").split(" ");
    const firstName = names[0] || "";
    const lastName = names.slice(1).join(" ") || "";
    const token = await user.getIdToken();
    localStorage.setItem('accessToken', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;

    try {
      await axios.post('/api/anvandare/v1', {
        email: user.email,
        förnamn: firstName,
        efternamn: lastName,
        personNummer: null, // To be filled during onboarding
        telefonNummer: null, // To be filled during onboarding
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } catch (e) {
      console.error("Error creating user in backend", e);
    }
  }
  return result;
};



/** **************************************
 * Sign up
 *************************************** */
export const signUp = async ({
  email,
  password,
  firstName,
  lastName,
  personNummer,
  telefonNummer,
}: SignUpParams): Promise<void> => {
  try {
    let user;
    try {
      const newUser = await _createUserWithEmailAndPassword(
        firebaseClientAuth,
        email,
        password
      );
      user = newUser.user;
    } catch (firebaseError: any) {
      if (firebaseError.code === 'auth/email-already-in-use') {
        const credentials = await _signInWithEmailAndPassword(
          firebaseClientAuth,
          email,
          password
        );
        user = credentials.user;
      } else {
        throw firebaseError;
      }
    }

    /*
     * (1) If skip emailVerified
     * Remove :
     * await _sendEmailVerification(newUser.user);
     */
    const actionCodeSettings = {
      url: `${window.location.origin}${paths.authAction}`,
      handleCodeInApp: true,
    };

    if (!user.emailVerified) {
      _sendEmailVerification(user, actionCodeSettings).catch((err) =>
        console.error('Error sending email verification', err)
      );
    }

    // 1. Create User in Backend (Priority)
    try {
      const token = await user.getIdToken();
      localStorage.setItem('accessToken', token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      await axios.post('/api/anvandare/v1', {
        email: email,
        förnamn: firstName,
        efternamn: lastName,
        personNummer: personNummer,
        telefonNummer: telefonNummer,
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } catch (e) {
      console.error("Error creating user in backend", e);
      // We don't throw here to allow the flow to continue, 
      // but you might want to alert the user or retry.
    }

    // User is created in backend above. Firestore is legacy/not used.


  } catch (error) {
    console.error("Error during sign up:", error);
    throw error;
  }
};

/** **************************************
 * Sign out
 *************************************** */
export const signOut = async (): Promise<void> => {
  localStorage.removeItem('accessToken');
  delete axios.defaults.headers.common.Authorization;
  await _signOut(firebaseClientAuth);
};

/** **************************************
 * Change password
 *************************************** */
export type ÄndraLösenordParams = {
  nuvarandeLösenord: string;
  nyttLösenord: string;
};

/** Returnerar true om den inloggade användaren har ett lösenord (dvs. inte enbart Google/Facebook). */
export const harLösenordsinloggning = (): boolean =>
  !!firebaseClientAuth.currentUser?.providerData.some(
    (provider) => provider.providerId === _EmailAuthProvider.PROVIDER_ID
  );

export const ändraLösenord = async ({
  nuvarandeLösenord,
  nyttLösenord,
}: ÄndraLösenordParams): Promise<void> => {
  const user = firebaseClientAuth.currentUser;

  if (!user?.email) {
    throw new Error('Ingen inloggad användare.');
  }

  // Firebase kräver en färsk inloggning innan lösenordet får bytas.
  const credential = _EmailAuthProvider.credential(user.email, nuvarandeLösenord);
  await _reauthenticateWithCredential(user, credential);

  await _updatePassword(user, nyttLösenord);
};

/** **************************************
 * Reset password
 *************************************** */
export const sendPasswordResetEmail = async ({
  email,
}: ForgotPasswordParams): Promise<void> => {
  const actionCodeSettings = {
    url: `${window.location.origin}${paths.authAction}`,
    handleCodeInApp: true,
  };
  await _sendPasswordResetEmail(firebaseClientAuth, email, actionCodeSettings);
};
